import {
  Home,
  Settings,
  Heart,
  MessageCircle,
  Gem,
  Users,
  Compass,
  Sparkles,
  Music,
  BookOpen,
  Lock,
  Film
} from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import logo from "@/assets/logo-circle.png";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { NavLink } from "@/components/NavLink";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
  SidebarTrigger,
} from "@/components/ui/sidebar";
// 假设你的 logo 图片路径如下，请根据实际情况调整
// import logo from "@/assets/logo-circle.png"; 

// ⭐ 这里改：Today’s Gem → /main
const mainItems = [
  { titleKey: "nav.today", fallback: "Today's Gem", url: "/main", icon: Home },
  { titleKey: "nav.myGems", fallback: "My Gems", url: "/my-gems", icon: BookOpen },
  { titleKey: "nav.lifeEditor", fallback: "Life Editor", url: "/life-editor", icon: Film },
  // 单独的 Mentor Deep Dive，排在 Life Editor 下面
  { titleKey: "nav.mentor", fallback: "Mentor Deep Dive", url: "/mentor", icon: Compass },
];

// Coming Soon 菜单（仅保留冥想库，社区与五维文明暂时隐藏）
const comingSoonItems = [
  {
    titleKey: "nav.meditation",
    fallback: "Meditation Library",
    url: "/meditation",
    icon: Music,
  },
];


export function AppSidebar() {
  const { t } = useTranslation();
  const { state } = useSidebar();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user?.email) {
        // 这里暂时没用到 email，就不处理
      }
    };
    loadUser();
  }, []);

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) return;

    setIsSendingFeedback(true);

    // Pre-open a tab synchronously so the app page never navigates away.
    // If popups are blocked, this will be null and we'll keep the user in-app.
    const draftTab = window.open("about:blank", "_blank");
    if (draftTab) {
      try {
        // Best-effort: prevent the new tab from having access to this window.
        (draftTab as any).opener = null;
      } catch {
        // ignore
      }
    }

    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id ?? null;
      const email = userData?.user?.email || null;

      await supabase.from("feedback").insert({
        user_id: userId,
        email,
        message: feedbackMessage.trim(),
        meta: {
          source: "innergem-pwa",
          path: window.location.pathname,
        },
      });

      const subject = encodeURIComponent("Inner Gem feedback");
      const bodyLines = [
        feedbackMessage.trim(),
        "",
        "---",
        email ? `From: ${email}` : "",
      ];
      const body = encodeURIComponent(bodyLines.join("\n"));

      const to = encodeURIComponent("innergem1313@gmail.com");
      const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;

      // Navigate the pre-opened tab to Gmail compose.
      if (draftTab) {
        draftTab.location.href = gmailComposeUrl;
      } else {
        // Popup blocked: do NOT navigate away from the app.
        console.warn(
          "Feedback email tab blocked by the browser (popup blocked).",
          { gmailComposeUrl },
        );
      }

      setIsFeedbackOpen(false);
      setFeedbackMessage("");
    } catch (err) {
      console.error("Failed to send feedback:", err);
      try {
        draftTab?.close();
      } catch {
        // ignore
      }
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const isCollapsed = state === "collapsed";

  return (
    <Sidebar collapsible="icon" className="border-r border-border z-40">
      {/* Header / Logo Area */}
      <SidebarHeader className="p-4 pb-2">
        <div className="pt-4 pb-2 px-0 flex flex-col gap-4">
          <SidebarTrigger
            className={
              "inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background/70 hover:bg-accent " +
              (isCollapsed ? "self-center" : "self-start")
            }
          />

          {!isCollapsed && (
            <div className="flex flex-col items-center gap-1 animate-in fade-in">
              <div className="relative w-20 h-20 rounded-full overflow-hidden shadow-lg border-2 border-white/50 bg-white">
                <img src={logo} alt={t("app.name", { defaultValue: "Inner Gem" })} className="w-full h-full object-cover" onError={(e) => e.currentTarget.src = "https://placehold.co/64x64/purple/white?text=IG"} />
              </div>
              <span className="text-lg font-semibold tracking-tight text-purple-900">
                {t("app.name", { defaultValue: "Inner Gem" })}
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-0">
        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.title", { defaultValue: "Navigation" })}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* 主功能：Today / My Gems / Life Editor / Mentor */}
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton asChild className="py-5" tooltip={t(item.titleKey, { defaultValue: item.fallback })}>
                    <NavLink to={item.url} className="flex items-center gap-3">
                      <item.icon className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        {t(item.titleKey, { defaultValue: item.fallback })}
                      </span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Coming Soon 页面（当前只展示冥想库），样式与主导航一致 */}
              {comingSoonItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton
                    asChild
                    className="py-5"
                    tooltip={t(item.titleKey, { defaultValue: item.fallback })}
                  >
                    <NavLink to={item.url} className="flex items-center gap-3">
                      <item.icon className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        {t(item.titleKey, { defaultValue: item.fallback })}
                      </span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="p-2">
        <SidebarMenu>
          {/* Settings */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="hover:bg-accent" tooltip={t("nav.settings", { defaultValue: "Settings" })}>
              <NavLink to="/settings" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                {!isCollapsed && <span>{t("nav.settings", { defaultValue: "Settings" })}</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Feedback */}
          <SidebarMenuItem>
            <Dialog open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen}>
              <DialogTrigger asChild>
                <SidebarMenuButton className="hover:bg-accent" tooltip={t("nav.feedback", { defaultValue: "Feedback" })}>
                  <MessageCircle className="h-4 w-4" />
                  {!isCollapsed && <span>{t("nav.feedback", { defaultValue: "Feedback" })}</span>}
                </SidebarMenuButton>
              </DialogTrigger>

              <DialogContent className="max-w-lg w-[92vw] max-w-xs rounded-2xl">
                <DialogHeader>
                  <DialogTitle>{t("feedback.title", { defaultValue: "Send feedback" })}</DialogTitle>
                  <DialogDescription className="sr-only">
                    Share your feedback with Inner Gem.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                  <Textarea
                    placeholder={t("feedback.placeholder", { defaultValue: "What would you like to share with Inner Gem?" })}
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    className="min-h-[140px]"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsFeedbackOpen(false)}
                    >
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSendFeedback}
                      disabled={isSendingFeedback || !feedbackMessage.trim()}
                    >
                      {isSendingFeedback ? t("feedback.sending", { defaultValue: "Sending..." }) : t("feedback.sendOpenEmail", { defaultValue: "Send & open email" })}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("feedback.footer", { defaultValue: "Your message will be saved securely and also opened in Gmail, addressed to innergem1313@gmail.com." })}
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </SidebarMenuItem>

          {/* ✨ Support Inner Gem (New Integrated Version) ✨ */}
          <SidebarMenuItem>
            <Dialog>
              <DialogTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="
                    w-full justify-center
                    rounded-full border border-white/20
                    bg-gradient-to-r from-[#C9A961] via-[#F4E4B7] to-[#C9A961]
                    px-5 py-6 text-sm font-semibold
                    text-slate-900
                    shadow-[0_12px_22px_rgba(232,191,91,0.45)]
                    hover:brightness-105
                    transition-all duration-300
                    mt-4
                  "
                  tooltip={t("support.title", { defaultValue: "Support Inner Gem" })}
                >
                  <Heart className="h-5 w-5 text-slate-900 fill-slate-900/10" />
                  {!isCollapsed && <span className="ml-2">{t("support.unlock", { defaultValue: "Unlock / Support" })}</span>}
                </SidebarMenuButton>
              </DialogTrigger>

              {/* 弹窗内容 */}
              <DialogContent
                className="
                  max-w-lg w-[92vw] sm:w-full 
                  max-h-[85vh] 
                  flex flex-col 
                  p-0
                  gap-0
                  overflow-hidden
                  rounded-2xl
                "
              >
                {/* 头部 */}
                <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                  <DialogTitle className="text-2xl text-center font-bold text-purple-900 flex flex-col items-center gap-3">
                    <div className="p-3 bg-purple-50 rounded-full">
                      <Heart className="w-8 h-8 text-red-500 fill-red-500" />
                    </div>
                    {t("support.title", { defaultValue: "Support Inner Gem" })}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {t("support.dialogDesc", { defaultValue: "Information about how to support Inner Gem and Tres Ong." })}
                  </DialogDescription>
                </DialogHeader>

                {/* 正文：可滚动区域 */}
                <div
                  className="
                    flex-1
                    px-6
                    pb-4
                    space-y-4
                    text-[15px] sm:text-sm
                    text-gray-600
                    leading-relaxed
                    overflow-y-auto
                  "
                >
                  <p>{t("support.modal.intro1", { defaultValue: "I'm Tres, the solo creator behind Inner Gem. My deepest wish is that more and more of us can gently transition from the dense, fast-paced 3D world into a 5D New Earth — a way of living with more awareness, compassion, and inner freedom." })}</p>
                  <p>{t("support.modal.intro2", { defaultValue: "Inner Gem is not the destination; it's a small doorway, a humble companion that walks with you as you remember who you really are." })}</p>
                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 text-purple-900 font-medium italic">
                    <p>{t("support.modal.quote", { defaultValue: "When you remember you are the director, you no longer fight these actors. You acknowledge them, guide them, and let them step back when their scene is complete." })}</p>
                  </div>
                  <p>{t("support.modal.body1", { defaultValue: "Your support helps me continue building features, guides, and spaces that remind you of this truth: you are not your passing emotions; you are the consciousness that can see them, hold them, and lovingly direct the story of your life." })}</p>

                  <div className="my-6 p-4 bg-purple-50 rounded-lg border border-purple-100 text-sm">
                    <h4 className="font-semibold text-purple-900 mb-2">
                      {t("support.modal.membershipTitle", { defaultValue: "Membership Options" })}
                    </h4>
                    <p className="text-purple-800 mb-3">
                      {t("support.modal.membershipIntro", {
                        defaultValue:
                          "Choose a membership tier that fits your season.",
                      })}
                    </p>
                    <ul className="list-disc list-inside text-purple-800 space-y-1 ml-1">
                      <li>
                        <span className="font-semibold">
                          {t("settings.membership_beginner", { defaultValue: "Beginner" })}
                        </span>
                        <span className="ml-1">
                          {t("support.modal.membershipBeginnerPrice", {
                            defaultValue: "— SGD 250/year or SGD 25.99/month",
                          })}
                        </span>
                        <div className="ml-5 mt-1 text-xs text-purple-700/90 leading-snug">
                          {t("support.modal.membershipBeginnerHighlights", {
                            defaultValue:
                              "3 SPARK + 3 GUIDED per day; can browse mentors but cannot start 1:1 deep dive chat.",
                          })}
                        </div>
                      </li>
                      <li>
                        <span className="font-semibold">
                          {t("settings.membership_intermediate", { defaultValue: "Intermediate" })}
                        </span>
                        <span className="ml-1">
                          {t("support.modal.membershipIntermediatePrice", {
                            defaultValue: "— SGD 300/year or SGD 29.99/month",
                          })}
                        </span>
                        <div className="ml-5 mt-1 text-xs text-purple-700/90 leading-snug">
                          {t("support.modal.membershipIntermediateHighlights", {
                            defaultValue:
                              "5 SPARK + 5 GUIDED per day; can start 1:1 mentor deep dive at standard price.",
                          })}
                        </div>
                      </li>
                      <li>
                        <span className="font-semibold">
                          {t("settings.membership_upper_intermediate", { defaultValue: "Upper Intermediate" })}
                        </span>
                        <span className="ml-1">
                          {t("support.modal.membershipUpperIntermediatePrice", {
                            defaultValue: "— SGD 400/year or SGD 39.99/month",
                          })}
                        </span>
                        <div className="ml-5 mt-1 text-xs text-purple-700/90 leading-snug">
                          {t("support.modal.membershipUpperIntermediateHighlights", {
                            defaultValue:
                              "10 SPARK + 10 GUIDED per day; can start 1:1 mentor deep dive and unlock member-only discount.",
                          })}
                        </div>
                      </li>
                    </ul>
                    <p className="mt-3 text-xs text-purple-700/90 leading-snug">
                      {t("support.modal.membershipMentorCreditNote", {
                        defaultValue:
                          "Mentor Deep Dive is an add-on service: without a GEMMDD code, each question uses 1 paid credit; with an active GEMMDD pass, you can ask without per-question payment during the pass period.",
                      })}
                    </p>
                  </div>

                  <div className="my-4">
                    <stripe-pricing-table
                      pricing-table-id="prctbl_1TuCIvF701n8ruTe8SaUAHjC"
                      publishable-key="pk_live_51SR3mtF701n8ruTeUIDGJA4FV5ajQtTm0cb2Ce60Xd1sjAUESmGcQFwbNLUR9cAaT8a8jJXAxMXYeW2uzQUld64z00HryIrw8P"
                    ></stripe-pricing-table>
                  </div>

                  <div className="text-right pt-2">
                    <p className="font-serif italic text-gray-500">{t("support.modal.signatureThanks", { defaultValue: "With gratitude," })}</p>
                    <p className="font-bold text-gray-800">{t("support.modal.signatureName", { defaultValue: "Tres Ong" })}</p>
                    <p className="text-xs text-gray-400">{t("support.modal.signatureRole", { defaultValue: "Founder, Inner Gem" })}</p>
                  </div>
                </div>

                {/* Stripe pricing table handles its own checkout buttons now; no extra footer CTA needed. */}
              </DialogContent>
            </Dialog>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}